(function () {
  if (customElements.get('product-card-carousel')) return;

  var ANIM = { duration: 180, easing: 'ease-out' };
  /* The class a frame wears while it is borrowed for a crossfade (below). Its one rule is in
     `assets/c2-visual.css`, beside the fade's duration and curve. */
  var OVER = 'product-card__carousel-image--over';
  var IS_TOUCH = window.matchMedia('(pointer: coarse)').matches;
  var IS_HOVER = window.matchMedia('(pointer: fine)').matches;
  var PREFERS_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ProductCardCarousel extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;

      if (this.querySelector('.product-card__carousel-track')) {
        this._init();
      } else {
        this._initFrame = requestAnimationFrame(() => {
          this._initFrame = null;
          if (this.isConnected) this._init();
        });
      }
    }

    _init() {
      if (this._initialized) return;

      this.track = this.querySelector('.product-card__carousel-track');
      this.slides = Array.from(this.querySelectorAll('.product-card__carousel-image:not(.product-card__carousel-clone)'));
      this.figure = this.closest('.product-card__figure');
      this.prevBtn = this.figure?.querySelector('.product-card__carousel-prev');
      this.nextBtn = this.figure?.querySelector('.product-card__carousel-next');
      this.dots = Array.from(this.figure?.querySelectorAll('.product-card__carousel-dot') || []);
      this.currentIndex = 0;
      this.slideCount = this.slides.length;
      this._didSwipe = false;
      this._anim = null;
      this._wrapSlide = null;
      this._wrapSlideTransform = '';
      this._fade = null;
      this._usePx = IS_TOUCH;
      this._slideW = 0;
      this._requestedIndex = null;

      if (this.slideCount <= 1 || !this.track) return;

      this._initialized = true;
      this._listeners = new AbortController();
      var signal = this._listeners.signal;

      /* Real slides only: edge wrapping temporarily repositions the real boundary
         slide instead of creating a second, fetch-bearing image element. */
      this._setPct(0);
      this._syncA11y();

      this.prevBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.go(-1);
      }, { signal });

      this.nextBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.go(1);
      }, { signal });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this._revealArrows();
          var target = Number(dot.dataset.carouselIndex);
          if (Number.isNaN(target)) return;
          var logical = ((this.currentIndex % this.slideCount) + this.slideCount) % this.slideCount;
          if (target === logical && this._requestedIndex == null) return;
          this._requestedIndex = null;
          this.go(target - logical);
        }, { signal });
      });

      /* Touch swipe */
      this._touchStartX = 0;
      this._touchStartY = 0;
      this._touchDeltaX = 0;
      this._swipeDir = null;

      this.addEventListener('touchstart', (event) => {
        this._cancelAnim();
        this._touchStartX = event.touches[0].clientX;
        this._touchStartY = event.touches[0].clientY;
        this._touchDeltaX = 0;
        this._didSwipe = false;
        this._swipeDir = null;
        if (this._usePx) this._measureSlide();
      }, { passive: true, signal });

      this.addEventListener('touchmove', (event) => {
        const dx = event.touches[0].clientX - this._touchStartX;
        const dy = event.touches[0].clientY - this._touchStartY;

        if (!this._swipeDir && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
          this._swipeDir = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
        }

        if (this._swipeDir !== 'h') return;

        this._revealArrows();
        event.preventDefault();
        this._touchDeltaX = dx;
        this._didSwipe = true;
        var neighbor = this.currentIndex + (dx < 0 ? 1 : -1);
        this._loadSlide(neighbor);
        var slide = this.slides[((neighbor % this.slideCount) + this.slideCount) % this.slideCount];
        // Keep the photographed frame visible while the requested neighbor decodes.
        if (slide?.complete && slide.naturalWidth > 0) {
          this._showSlides();
          this._setPosPx(this._px(this.currentIndex) + dx);
        }
      }, { passive: false, signal });

      this.addEventListener('touchend', () => {
        if (this._didSwipe && Math.abs(this._touchDeltaX) > 40) {
          this._revealArrows();
          this.go(this._touchDeltaX < 0 ? 1 : -1);
        } else if (this._didSwipe) {
          this._animateTo(this.currentIndex);
        }
      }, { passive: true, signal });

      this.addEventListener('touchcancel', () => {
        if (this._didSwipe) this._animateTo(this.currentIndex);
        this._didSwipe = false;
      }, { passive: true, signal });

      this.addEventListener('click', (event) => {
        if (this._didSwipe) {
          event.preventDefault();
          this._didSwipe = false;
        }
      }, { signal });

      this._observer = new MutationObserver(() => this._onDomChange());
      this._observer.observe(this, { childList: true, subtree: true });

      if (this._usePx) {
        this._onResize = () => {
          if (this._slideW === 0) return;
          this._measureSlide();
          this._setPosPx(this._px(this.currentIndex));
        };
        window.addEventListener('resize', this._onResize, { passive: true, signal });
      }

      if (IS_HOVER && this.slideCount > 1 && this.figure && this.getAttribute('secondary-on-hover') === 'true') {
        this._onHoverEnter = () => this._showSecondaryImage();
        this._onHoverLeave = () => this._showPrimaryImage();
        this.figure.addEventListener('mouseenter', this._onHoverEnter, { signal });
        this.figure.addEventListener('mouseleave', this._onHoverLeave, { signal });
      }

      /* The server renders controls hidden. Expose them only after every
         interaction and state-sync dependency above is ready. */
      this._setControlsReady(true);
    }

    disconnectedCallback() {
      this._navigationId = (this._navigationId || 0) + 1;
      this._requestedIndex = null;
      this.removeAttribute('aria-busy');
      if (this._initFrame) cancelAnimationFrame(this._initFrame);
      this._initFrame = null;
      this._listeners?.abort();
      this._listeners = null;
      this._anim?.cancel();
      this._anim = null;
      this._settleFade();
      this._clearWrapSlide();
      this._observer?.disconnect();
      clearTimeout(this._arrowTimer);
      this.figure?.classList.remove('carousel-interacted');
      this._setControlsReady(false);
      this._initialized = false;
    }

    _setControlsReady(ready) {
      [this.prevBtn, this.nextBtn].forEach((button) => {
        if (!button) return;

        if (ready) {
          button.style.removeProperty('display');
          button.removeAttribute('hidden');
        } else {
          button.setAttribute('hidden', '');
          /* The theme gives these controls `display: flex`, which can override
             the user-agent [hidden] rule. Keep the SSR inline fallback in sync. */
          button.style.display = 'none';
        }
      });

      if (ready) {
        this.setAttribute('data-carousel-ready', '');
      } else {
        this.removeAttribute('data-carousel-ready');
      }
    }

    _showSecondaryImage() {
      var connection = window.navigator?.connection;
      if (connection?.saveData || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '')) return;
      this._fadeTo(1);
    }

    _showPrimaryImage() {
      this._fadeTo(0);
    }

    _fadeTo(index) {
      if (!this.track || this.slideCount <= 1) return;
      var request = this._navigationId = (this._navigationId || 0) + 1;
      this._requestedIndex = index;
      this.removeAttribute('aria-busy');
      var ready = this._loadSlide(index);
      if (ready) {
        ready.then((loaded) => {
          if (!this.isConnected || request !== this._navigationId) return;
          this._requestedIndex = null;
          if (loaded) this._fadeToReady(index);
        });
        return;
      }
      this._requestedIndex = null;
      this._fadeToReady(index);
    }

    _fadeToReady(index) {
      if (!this.track || this.slideCount <= 1) return;
      var to = ((index % this.slideCount) + this.slideCount) % this.slideCount;
      if (to === 1) this._warmNext(to);

      if (this._fade) {
        var running = this._fade;
        if (to === running.target) return;
        if (to === running.top || to === running.base) {
          running.target = to;
          this._aimFade(running);
          this.currentIndex = to;
          this._syncA11y();
          return;
        }
        this._settleFade();
      }

      this._cancelAnim();
      var from = ((this.currentIndex % this.slideCount) + this.slideCount) % this.slideCount;
      if (from === to) {
        this._land(from);
        this._syncA11y();
        return;
      }

      /* A slide animation cancelled a moment ago leaves the track between two cells. Land it before
         the fade starts, so the two frames the fade stacks are the two the shopper is looking at. */
      this.currentIndex = from;
      this._land(from);

      var topEl = this.slides[to];
      /* The resting frame must exist under the borrowed one; it is never touched, only relied on. */
      if (!topEl || !this.slides[from]) return;

      this._holdStill([topEl], () => {
        topEl.style.transform = 'translate3d(' + (from - to) * 100 + '%,0,0)';
        topEl.classList.add(OVER);
        topEl.style.opacity = '0';
      });

      var fade = { base: from, top: to, target: to, topEl: topEl, timer: null, onEnd: null };
      fade.onEnd = (event) => {
        if (event.propertyName !== 'opacity' || event.target !== topEl) return;
        this._settleFade();
      };
      this.track.addEventListener('transitionend', fade.onEnd);
      this._fade = fade;
      this.currentIndex = to;
      this._syncA11y();

      if (topEl.complete && topEl.naturalWidth > 0) {
        this._aimFade(fade);
      } else {
        var start = () => {
          if (this._fade === fade && fade.target === fade.top) this._aimFade(fade);
        };
        topEl.decode().then(start, start);
      }
    }

    _aimFade(fade) {
      fade.topEl.style.opacity = fade.target === fade.top ? '1' : '0';
      clearTimeout(fade.timer);
      /* `transitionend` is the signal. The timer is the net under it, for a stylesheet that resolves
         the transition to nothing (no transition, no event); it reads the frame's own duration, so
         the fade's length is never restated here, plus a short grace. */
      fade.timer = setTimeout(() => this._settleFade(), this._fadeMs(fade.topEl) + 100);
    }

    _fadeMs(el) {
      var raw = getComputedStyle(el).transitionDuration.split(',')[0].trim();
      var ms = raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
      return isNaN(ms) ? 0 : ms;
    }

    _settleFade() {
      var fade = this._fade;
      if (!fade) return;
      this._fade = null;
      clearTimeout(fade.timer);
      this.track?.removeEventListener('transitionend', fade.onEnd);

      this._holdStill([fade.topEl], () => {
        fade.topEl.style.transform = '';
        fade.topEl.style.opacity = '';
        fade.topEl.classList.remove(OVER);
        if (this.track) this._land(fade.target);
      });
      this.currentIndex = fade.target;
      this._syncA11y();
    }

    _land(index) {
      if (this._usePx) {
        this._measureSlide();
        this._setPosPx(this._px(index));
      } else {
        this._setPct(index);
      }
    }

    _holdStill(elements, change) {
      elements.forEach((el) => el.style.setProperty('transition', 'none', 'important'));
      change();
      void (this.track || elements[0]).offsetWidth;
      elements.forEach((el) => el.style.removeProperty('transition'));
    }

    _prepareWrap(index) {
      this._clearWrapSlide();

      var slide;
      var distance;

      if (index >= this.slideCount) {
        slide = this.slides[0];
        distance = this.slideCount * 100;
      } else if (index < 0) {
        slide = this.slides[this.slideCount - 1];
        distance = -this.slideCount * 100;
      } else {
        return;
      }

      this._wrapSlide = slide;
      this._wrapSlideTransform = slide.style.transform;

      slide.style.transform = 'translate3d(' + distance + '%,0,0)';
    }

    _clearWrapSlide() {
      if (!this._wrapSlide) return;
      this._wrapSlide.style.transform = this._wrapSlideTransform;
      this._wrapSlide = null;
      this._wrapSlideTransform = '';
    }

    _showSlides(indices) {
      this.slides.forEach((slide, index) => {
        slide.style.visibility = !indices || indices.includes(index) ? '' : 'hidden';
      });
    }

    _syncA11y() {
      if (!this.slideCount) return;
      var logicalIndex = ((this.currentIndex % this.slideCount) + this.slideCount) % this.slideCount;
      this.slides.forEach((slide, index) => {
        // Empty-alt card photographs remain decorative; the parent link names the product.
        slide.setAttribute('aria-hidden', index === logicalIndex && slide.getAttribute('alt') !== '' ? 'false' : 'true');
      });

      var position = (this.dataset.positionTemplate || '').replace('__INDEX__', String(logicalIndex + 1));
      [this.prevBtn, this.nextBtn].forEach((button) => {
        if (!button) return;
        var label = button.dataset.carouselLabel || '';
        button.setAttribute('aria-label', position ? label + '. ' + position : label);
      });

      // Settled neighbors must not bleed into the viewport at fractional pixel widths.
      this._showSlides(this._fade ? [this._fade.base, this._fade.top] : [logicalIndex]);
      this._syncDots();
    }

    /* Derives its own logical index rather than taking one, so it is safe to call before `go` has
       normalised `currentIndex` out of its wrap range: at slideCount the modulo is 0 and at -1 it is
       slideCount - 1, which are the frames the animation is travelling to in each case. */
    _syncDots() {
      if (!this.slideCount || !this.dots) return;
      var logicalIndex = ((this.currentIndex % this.slideCount) + this.slideCount) % this.slideCount;
      /* `aria-current` rather than a class, so the state is in the accessibility tree as well as in
         the paint. */
      this.dots.forEach((dot, index) => {
        if (index === logicalIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    _measureSlide() {
      this._slideW = this.getBoundingClientRect().width || this._slideW || 0;
    }

    _px(index) {
      return -(index * this._slideW);
    }

    _pct(index) {
      return -(index * 100);
    }

    /* Percentage positioning: used for initial render and desktop animations */
    _setPct(index) {
      this.track.style.transform = 'translate3d(' + this._pct(index) + '%,0,0)';
    }

    /* Pixel positioning: used for touch device animations */
    _setPosPx(px) {
      this.track.style.transform = 'translate3d(' + px + 'px,0,0)';
    }

    _cancelAnim() {
      /* A fade in flight settles at its target first: an arrow pressed mid-fade pages on from the
         frame the fade was going to, which is the frame the indicator already names. */
      this._settleFade();

      var hadWrapSlide = Boolean(this._wrapSlide);

      if (this._anim) {
        var currentTransform = getComputedStyle(this.track).transform;
        this._anim.cancel();
        this._anim = null;

        if (!hadWrapSlide && currentTransform && currentTransform !== 'none') {
          this.track.style.transform = currentTransform;
        }
      }

      if (hadWrapSlide) {
        this.currentIndex = ((this.currentIndex % this.slideCount) + this.slideCount) % this.slideCount;
        this._clearWrapSlide();

        if (this._usePx) {
          this._measureSlide();
          this._setPosPx(this._px(this.currentIndex));
        } else {
          this._setPct(this.currentIndex);
        }

        this._syncA11y();
      }
    }

    _animateTo(index, then, wrap) {
      this._cancelAnim();
      if (wrap) this._prepareWrap(index);
      this._showSlides();

      if (this._usePx) {
        this._measureSlide();
        if (this._slideW === 0) {
          if (then) then();
          this._syncA11y();
          return;
        }
      }
      /* Read computed position for seamless from value (handles % to px bridge). */
      var currentTransform = getComputedStyle(this.track).transform;
      var from = currentTransform && currentTransform !== 'none' ? currentTransform : this.track.style.transform;
      var to;
      if (this._usePx) {
        to = 'translate3d(' + this._px(index) + 'px,0,0)';
        this._setPosPx(this._px(index));
      } else {
        to = 'translate3d(' + this._pct(index) + '%,0,0)';
        this._setPct(index);
      }
      if (PREFERS_REDUCED_MOTION.matches) {
        if (then) then();
        this._syncA11y();
        return;
      }
      this._anim = this.track.animate(
        [{ transform: from }, { transform: to }],
        ANIM,
      );
      this._anim.onfinish = () => {
        this._anim = null;
        if (then) then();
        this._syncA11y();
      };
    }

    _warmNext(index) {
      var connection = window.navigator?.connection;
      if (connection?.saveData || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '')) return;
      // One forward neighbor of an engaged card, never the whole grid or gallery.
      this._loadSlide(index + 1, true);
    }

    _loadSlide(index, speculative = false) {
      var slide = this.slides[((index % this.slideCount) + this.slideCount) % this.slideCount];
      if (!slide) return null;
      if (slide._imageReady) {
        if (!speculative) slide.fetchPriority = 'auto';
        return slide._imageReady;
      }
      var source = slide.getAttribute('data-src');
      if (!source) return null;
      // Sources stay deferred until engagement; warm only its next likely frame.
      // Set sizes (already in HTML) and srcset before src so only the selected rendition is fetched.
      slide.loading = 'eager';
      slide.fetchPriority = speculative ? 'low' : 'auto';
      var sourceSet = slide.getAttribute('data-srcset');
      if (sourceSet) slide.setAttribute('srcset', sourceSet);
      slide.setAttribute('src', source);
      slide.removeAttribute('data-srcset');
      slide.removeAttribute('data-src');
      slide._imageReady = slide.decode().then(() => true, () => false).then((ready) => {
        slide._imageReady = null;
        if (!ready) {
          // WebKit otherwise retains the failed source when the same URL is retried.
          slide.removeAttribute('srcset');
          slide.removeAttribute('src');
          // Retain the current visible frame on failure and allow a later explicit retry.
          slide.setAttribute('data-src', source);
          if (sourceSet) slide.setAttribute('data-srcset', sourceSet);
        }
        return ready;
      });
      return slide._imageReady;
    }

    go(direction) {
      if (!this.track || this.slideCount <= 1) return;
      this._revealArrows();
      var request = this._navigationId = (this._navigationId || 0) + 1;
      // Keep intent separate from the decoded frame so clicks made during loading
      // accumulate and a pending hover has the same next frame as a finished hover.
      var base = this._requestedIndex ?? this.currentIndex;
      var target = ((base + direction) % this.slideCount + this.slideCount) % this.slideCount;
      this._requestedIndex = target;
      var commit = (loaded) => {
        if (!this.isConnected || request !== this._navigationId) return;
        this._requestedIndex = null;
        this.removeAttribute('aria-busy');
        if (!loaded) return;
        var current = ((this.currentIndex % this.slideCount) + this.slideCount) % this.slideCount;
        var step = target - current;
        if (direction > 0 && step < 0) step += this.slideCount;
        if (direction < 0 && step > 0) step -= this.slideCount;
        this._goReady(step);
        this._warmNext(target);
      };
      var ready = this._loadSlide(target);
      if (ready) {
        this.setAttribute('aria-busy', 'true');
        ready.then(commit);
      } else {
        commit(true);
      }
    }

    _goReady(direction) {
      if (!this.track || this.slideCount <= 1) return;
      // Rapid clicks and dots can skip unloaded cells: crossfade the endpoints
      // instead of sliding through photographs that have not arrived yet.
      if (Math.abs(direction) > 1) {
        this._fadeToReady(this.currentIndex + direction);
        return;
      }
      this._revealArrows();
      this._cancelAnim();
      if (this._usePx) this._measureSlide();
      this.currentIndex += direction;
      var wrap = this.currentIndex >= this.slideCount || this.currentIndex < 0;

      this._syncDots();

      this._animateTo(this.currentIndex, () => {
        var wrapped = false;
        if (this.currentIndex >= this.slideCount) {
          this.currentIndex = 0;
          wrapped = true;
        } else if (this.currentIndex < 0) {
          this.currentIndex = this.slideCount - 1;
          wrapped = true;
        }
        if (wrapped) {
          this._clearWrapSlide();

          /* The boundary slide was visually adjacent during the animation. Jump
             the track to that same real slide before the next paint. */
          if (this._usePx && this._slideW > 0) {
            this._setPosPx(this._px(this.currentIndex));
          } else {
            this._setPct(this.currentIndex);
          }
        }
        this._syncA11y();
      }, wrap);
    }

    _revealArrows() {
      if (!this.figure) return;
      this.figure.classList.add('carousel-interacted');
      clearTimeout(this._arrowTimer);
      this._arrowTimer = setTimeout(() => {
        this.figure.classList.remove('carousel-interacted');
      }, 1000);
    }

    _onDomChange() {
      this._navigationId = (this._navigationId || 0) + 1;
      this._requestedIndex = null;
      this.removeAttribute('aria-busy');
      this._observer?.disconnect();
      this._anim?.cancel();
      this._anim = null;
      this._settleFade();
      this._clearWrapSlide();
      this.track = this.querySelector('.product-card__carousel-track');
      this.slides = Array.from(this.querySelectorAll('.product-card__carousel-image:not(.product-card__carousel-clone)'));
      this.slideCount = this.slides.length;
      this.currentIndex = 0;

      if (!this.track || this.slideCount <= 1) {
        this._setControlsReady(false);
        this._observer?.observe(this, { childList: true, subtree: true });
        return;
      }

      this._setPct(0);
      this._syncA11y();
      this._setControlsReady(true);
      this._observer?.observe(this, { childList: true, subtree: true });
    }
  }

  customElements.define('product-card-carousel', ProductCardCarousel);

  var ACK = 'product-card__size-label--acknowledged';

  document.addEventListener('click', function (event) {
    var size = event.target.closest && event.target.closest('.product-card__size');
    if (!size || size.disabled) return;

    var label = size.querySelector('.product-card__size-label');
    if (!label) return;

    label.classList.remove(ACK);
    void label.offsetWidth;
    label.classList.add(ACK);
  });

  document.addEventListener(
    'animationend',
    function (event) {
      if (event.target.classList && event.target.classList.contains(ACK)) {
        event.target.classList.remove(ACK);
      }
    },
    true,
  );
})();
